import { STATE_VERSION } from './flow/engine';
import type { ConversationState, Snapshot, TranscriptEntry } from './flow/types';

/**
 * Conversation state survives closing the widget and navigating between
 * pages of the same tab (sessionStorage, same-origin only). It holds the
 * customer's own answers, the visible transcript and status words the server
 * returned (lookup status, matched event name). It never holds secrets: the
 * single-use resend token, the verification code and the organizer's address
 * are stripped from the answers AND from every back-navigation snapshot, and the
 * customer's code bubble is masked. Expires after MAX_AGE_MS.
 */
const KEY = 'inv-sc-state-v' + STATE_VERSION;
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** Answer keys that must not be persisted in the browser (security review 2026-09-09, B4). */
export const VOLATILE_ANSWERS = ['actionToken', 'organizerEmail', 'emailCode'];

/** Customer bubbles of these steps are stored masked - the text IS the secret. */
const MASKED_STEPS: Record<string, string> = { enterCode: '••••••' };

function stripAnswers(answers: Record<string, unknown>): Record<string, unknown> {
	const copy = { ...answers };
	for (const k of VOLATILE_ANSWERS) {
		delete copy[k];
	}
	return copy;
}

function stripSnapshot(snap: Snapshot): Snapshot {
	return { ...snap, answers: stripAnswers(snap.answers ?? {}) };
}

function maskEntry(entry: TranscriptEntry): TranscriptEntry {
	const masked = entry.role === 'customer' ? MASKED_STEPS[entry.stepId] : undefined;
	return masked === undefined ? entry : { ...entry, text: masked };
}

/** Public for tests: the exact shape written to sessionStorage. */
export function toStoredState(state: ConversationState): ConversationState {
	return {
		...state,
		answers: stripAnswers(state.answers),
		stack: (state.stack ?? []).map(stripSnapshot),
		transcript: (state.transcript ?? []).map(maskEntry),
		pendingAction: null,
		actionError: null,
	};
}

export function saveState(state: ConversationState): void {
	try {
		sessionStorage.setItem(KEY, JSON.stringify(toStoredState(state)));
	} catch {
		/* storage unavailable (private mode, quota) — the widget still works */
	}
}

export function loadState(now = Date.now(), language?: string): ConversationState | null {
	try {
		const raw = sessionStorage.getItem(KEY);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw) as ConversationState;
		if (!parsed || parsed.version !== STATE_VERSION) {
			return null;
		}
		// transcript texts are stored already translated - a conversation started on an English page
		// must not reappear, half English, on a Slovak one (inviton.eu → Slovak event page)
		if (language && parsed.language !== language) {
			sessionStorage.removeItem(KEY);
			return null;
		}
		if (typeof parsed.updatedAt !== 'number' || now - parsed.updatedAt > MAX_AGE_MS) {
			sessionStorage.removeItem(KEY);
			return null;
		}
		if (!Array.isArray(parsed.transcript) || !Array.isArray(parsed.stack)) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}
